// src/Service/temperatureService.js
import { api, DEFAULT_DEPLOYMENT } from './fetchDeployments';
import store from '@/store';

function createEmptyTemperatureData() {
  return {
    times: [],
    values: [],
    latestDate: null,
    success: true,
    noDataAvailable: true
  };
}

export async function fetchTemperatureDepths(site) {
  try {
    if (!isValidSite(site)) {
      console.warn('Invalid site parameters');
      return {
        depths: [],
        success: false,
        error: 'Invalid parameters'
      };
    }

    const response = await api.get(`/metadata/${encodeURIComponent(site.trim())}`);
    
    if (!response.data || !response.data.verticalPosition) {
      return {
        depths: [],
        success: true,
        noDataAvailable: true
      };
    }

    const depths = response.data.verticalPosition
      .map(d => ({
        title: `${Math.abs(d)} meter`,
        value: Math.abs(d)
      }))
      .sort((a, b) => a.value - b.value);

    return {
      depths,
      success: true,
      noDataAvailable: depths.length === 0
    };
    
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Temperature depths fetch error:', error.message);
    }
    
    return {
      depths: [],
      success: false,
      error: 'Unable to fetch temperature depths'
    };
  }
}

export async function fetchTemperatureData(site, depth) {
  try {
    if (!isValidSite(site)) {
      return {
        ...createEmptyTemperatureData(),
        success: false,
        error: 'Invalid parameters'
      };
    }

    const response = await api.post('/waterTemperatureData', {
      site: site.trim(),
      depth: -depth
    });

    // Handle 204 No Content
    if (response.status === 204) {
      return {
        ...createEmptyTemperatureData(),
        success: true,
        noDataAvailable: true
      };
    }

    // Handle successful response
    if (response.status === 200 && response.data?.time) {
      const combinedData = response.data.time
        .map((time, index) => ({
          time: new Date(time),
          value: response.data.values[index]
        }))
        .filter(d => !isNaN(d.value))
        .sort((a, b) => a.time - b.time);

      if (combinedData.length === 0) {
        return {
          ...createEmptyTemperatureData(),
          success: true,
          noDataAvailable: true
        };
      }

      return {
        times: combinedData.map(d => d.time),
        values: combinedData.map(d => d.value),
        latestDate: new Date(Math.max(...combinedData.map(d => d.time))),
        success: true,
        noDataAvailable: false
      };
    }

    return {
      ...createEmptyTemperatureData(),
      success: true,
      noDataAvailable: true
    };

  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Temperature data fetch error:', error.message);
    }

    return {
      ...createEmptyTemperatureData(),
      success: false,
      error: 'Unable to fetch temperature data'
    };
  }
}

function isValidSite(site) {
  if (typeof site !== 'string' || !site.trim()) {
    return false;
  }

  if (site === DEFAULT_DEPLOYMENT.site) { // Changed from title to site
    return true;
  }

  const deployments = store.getters.getDeployments;
  if (!deployments) {
    return site === DEFAULT_DEPLOYMENT.site; // Changed from title to site
  }

  return deployments.some(deployment => deployment.site === site); // Changed from title to site
}

export {
  DEFAULT_DEPLOYMENT
};